/**
 * SPCB Branch Checkbox Module v1.18.25
 *
 * Replaces the branch shipping modal with an inline checkbox that:
 * 1. Appears below the "Entrega" (Delivery) section
 * 2. Toggles visibility of address fields (hides address_1, address_2, company when checked)
 * 3. Shows a "Calle de referencia" field when checked
 * 4. Filters shipping methods based on admin configuration
 * 5. Persists state in localStorage and cookie for backend sync
 *
 * v1.18.11: También actualizado forceSelectFirstVisibleRate() para respetar el modo.
 *           Esta función se llamaba desde fetchCartAndUpdateStore() y sobrescribía
 *           la selección correcta de filterShippingMethods().
 *
 * @package SuperPymeCheckoutBlocks
 */

/**
 * CRITICAL: Sync cookie from localStorage IMMEDIATELY on script load
 * v1.17.2: This ensures PHP has the correct branch mode state for validation
 * even before the checkbox is injected. Solves the issue where cookie was
 * cleared after order creation but localStorage persisted, causing validation
 * errors when user returns to checkout after a failed payment.
 */
(function syncCookieFromStorage() {
    try {
        var storedState = localStorage.getItem('spcb_branch_to_agency');
        if (storedState === 'true') {
            var expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString();
            document.cookie = 'spcb_branch_to_agency=true; expires=' + expires + '; path=/; SameSite=Lax';
        }
    } catch (e) {
        // Silently fail - localStorage might not be available
    }
})();

(function() {
    'use strict';

    // Debug mode
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB BranchCheckbox]', ...args);

    // Storage keys
    const STORAGE_KEY = 'spcb_branch_to_agency';
    const COOKIE_NAME = 'spcb_branch_to_agency';
    const REFERENCE_STORAGE_KEY = 'spcb_branch_reference';
    const REFERENCE_COOKIE_NAME = 'spcb_branch_reference';

    // Reference field selectors (avoid duplicate IDs with modal)
    const REFERENCE_INPUT_ID_INLINE = 'spcb-branch-reference-inline';
    const REFERENCE_INPUT_SELECTOR_INLINE = '[data-spcb-branch-reference="inline"]';

    // State
    let isCheckboxInjected = false;
    let isReferenceFieldInjected = false;
    let isReferenceFieldInjectionInProgress = false;  // v1.18.15 EC-11: Prevent race condition
    let checkboxElement = null;

    // v1.18.5: Flag global para que sistemas externos NO interfieran durante refresh
    // NOTA: Este flag NO se usa como guard interno, solo lo leen checkout-store-sync.js y checkout-shipping.js
    window.spcbBranchRefreshInProgress = false;

    // v1.18.24 EC-5: AbortController para cancelar fetches en progreso
    // HIPÓTESIS: Race condition entre GET /cart y POST /checkout causa duplicados
    // Si el usuario hace clic en "Pagar" mientras un fetch está en vuelo,
    // ambos requests pueden llamar create_order_shipping_lines() causando duplicados.
    // Solución: Cancelar cualquier fetch en progreso cuando se inicia el checkout.
    let cartFetchController = null;

    // v1.18.25 EC-5: Track de timeouts pendientes para cancelarlos en checkout
    // selectShippingRate() hace POST al servidor. Si hay timeouts pendientes
    // cuando el checkout inicia, pueden causar race conditions.
    // Solución: Cancelar TODOS los timeouts pendientes cuando checkout inicia.
    const pendingTimeouts = new Set();

    /**
     * v1.18.25: Tracked setTimeout - registra el timeout para poder cancelarlo
     *
     * IMPORTANTE: Incluye guard interno por si el timeout se ejecuta
     * en el mismo tick que cancelAllPendingTimeouts() (race condition de JS)
     *
     * @param {Function} callback - Función a ejecutar
     * @param {number} delay - Delay en ms
     * @returns {number} timeout ID
     */
    function trackedTimeout(callback, delay) {
        const id = setTimeout(() => {
            // Limpiar del Set primero
            pendingTimeouts.delete(id);

            // Guard: Si checkout ya inició, no ejecutar
            // Esto es una doble protección por si el timeout se ejecuta
            // justo antes de que cancelAllPendingTimeouts() lo cancele
            if (window.spcbCheckoutLock?.isProcessing?.()) {
                log('Tracked timeout skipped - checkout processing');
                return;
            }

            try {
                callback();
            } catch (e) {
                log('Tracked timeout error:', e.message);
            }
        }, delay);
        pendingTimeouts.add(id);
        return id;
    }

    /**
     * v1.18.25: Cancelar todos los timeouts pendientes
     * Llamado cuando el checkout inicia para prevenir race conditions
     */
    function cancelAllPendingTimeouts() {
        const count = pendingTimeouts.size;
        if (count > 0) {
            log('Cancelling', count, 'pending shipping sync timeouts');
            pendingTimeouts.forEach(id => {
                try {
                    clearTimeout(id);
                } catch (e) {
                    // Ignorar - el timeout ya pudo haber ejecutado
                }
            });
            pendingTimeouts.clear();
        }
    }

    // ============================================
    // STORAGE HELPERS
    // ============================================

    /**
     * Get the current state from localStorage
     */
    function getStoredState() {
        try {
            return localStorage.getItem(STORAGE_KEY) === 'true';
        } catch (e) {
            return false;
        }
    }

    /**
     * Save state to localStorage
     */
    function saveState(isChecked) {
        try {
            localStorage.setItem(STORAGE_KEY, isChecked ? 'true' : 'false');
            setCookie(COOKIE_NAME, isChecked ? 'true' : 'false', 1); // 1 hour
            log('State saved:', isChecked);
        } catch (e) {
            log('Error saving state:', e);
        }
    }

    /**
     * Set cookie for backend sync
     */
    function setCookie(name, value, hours) {
        const expires = new Date(Date.now() + hours * 60 * 60 * 1000).toUTCString();
        document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
    }

    /**
     * Get cookie value
     */
    function getCookie(name) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
    }

    // ============================================
    // SHIPPING RATES REFRESH (with guards)
    // ============================================

    let refreshDebounceTimer = null;
    let isRefreshing = false;

    /**
     * Check if checkout is currently processing
     * Uses the global spcbCheckoutLock if available
     */
    function isCheckoutProcessing() {
        // Check global lock
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return true;
        }
        // Check for processing class on checkout form
        if (document.querySelector('.wc-block-checkout.is-processing')) {
            return true;
        }
        // Check for checkout button in loading state
        if (document.querySelector('.wc-block-components-checkout-place-order-button[disabled]')) {
            return true;
        }
        return false;
    }

    /**
     * Check if a rate ID is an SPCB method
     */
    function isSpcbRate(rateId) {
        return typeof rateId === 'string' && rateId.startsWith('spcb_');
    }

    /**
     * Guard to prevent programmatic sync for non-SPCB rates
     * and avoid duplicate selections in a short window.
     */
    function shouldSkipProgrammaticSync(rateId, context = 'unknown') {
        if (!isSpcbRate(rateId)) {
            log('Skipping programmatic sync for non-SPCB rate:', rateId, context);
            return true;
        }

        const now = Date.now();
        const lock = window.spcbShippingSyncLock || { lastRateId: null, lastTs: 0 };
        if (lock.lastRateId === rateId && (now - lock.lastTs) < 500) {
            log('Shipping sync lock hit:', rateId, context);
            return true;
        }

        window.spcbShippingSyncLock = { lastRateId: rateId, lastTs: now };
        return false;
    }

    /**
     * Safely refresh shipping rates after branch checkbox change
     * v1.19.5: Fetch cart from server + receiveCart() to update Store
     *
     * This approach:
     * 1. Fetches /wc/store/v1/cart (server sees updated cookie)
     * 2. Server returns cart with filtered shipping rates
     * 3. dispatch.receiveCart() updates the entire Store
     * 4. WC Blocks re-renders with new rates
     */
    function safeRefreshShippingRates() {
        // Guard: Skip during checkout processing (CRÍTICO)
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('Checkout processing, skipping refresh');
            return;
        }

        // Debounce
        clearTimeout(refreshDebounceTimer);
        refreshDebounceTimer = setTimeout(() => {
            fetchCartAndUpdateStore();
        }, 150);
    }

    /**
     * Fetch cart from server and update WC Blocks Store
     * v1.19.5: The WORKING solution
     *
     * When we fetch /wc/store/v1/cart:
     * - Server reads the updated cookie (spcb_branch_to_agency)
     * - Server filters shipping rates accordingly
     * - We get back a cart object with the correct rates
     * - dispatch.receiveCart() updates the Store completely
     */
    async function fetchCartAndUpdateStore() {
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('Checkout processing, skipping fetch');
            return;
        }

        const wpData = window.wp?.data;
        if (!wpData) {
            log('wp.data not available');
            return;
        }

        const dispatch = wpData.dispatch('wc/store/cart');
        if (!dispatch) {
            log('Cart dispatch not available');
            return;
        }

        // v1.18.24 EC-5: Cancelar fetch anterior si existe (previene race condition)
        if (cartFetchController) {
            log('Aborting previous cart fetch');
            cartFetchController.abort();
        }
        cartFetchController = new AbortController();

        isRefreshing = true;
        window.spcbBranchRefreshInProgress = true;  // v1.18.5: Flag para sistemas externos
        log('Fetching cart from server...');

        try {
            // Get nonce for Store API
            const nonce = window.wcBlocksMiddlewareConfig?.nonce ||
                          window.wpApiSettings?.nonce || '';

            // Fetch current cart from server (server will return filtered rates)
            // v1.18.24 EC-5: Usar signal para permitir cancelación
            const response = await fetch('/wp-json/wc/store/v1/cart', {
                method: 'GET',
                credentials: 'same-origin',
                signal: cartFetchController.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'Nonce': nonce,
                    'X-WC-Store-API-Nonce': nonce
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const cartData = await response.json();
            log('Cart fetched successfully, shipping_rates:', cartData.shipping_rates?.length || 0);

            // Update the WC Blocks Store with the new cart data
            if (typeof dispatch.receiveCart === 'function') {
                dispatch.receiveCart(cartData);
                log('Store updated via receiveCart()');
            } else {
                log('receiveCart not available, trying alternative...');
                // Fallback: try to at least select a valid rate
            }

            // v1.18.6: Wait longer for React to re-render (300ms instead of 200ms)
            // React needs time to reconcile the new state after receiveCart()
            setTimeout(() => {
                // v1.18.28: Guard para prevenir race condition si checkout inició
                if (window.spcbCheckoutLock?.isProcessing?.()) return;

                try {
                    const isBranchMode = checkboxElement?.checked ?? getStoredState();
                    filterShippingMethods(isBranchMode);
                    forceSelectFirstVisibleRate();

                    // v1.18.6: Second pass after additional delay for slow renders
                    setTimeout(() => {
                        // v1.18.28: Guard para prevenir race condition si checkout inició
                        if (window.spcbCheckoutLock?.isProcessing?.()) return;

                        try {
                            filterShippingMethods(isBranchMode);
                            forceSelectFirstVisibleRate();
                            log('Cart refresh complete (second pass)');
                        } catch (innerErr) {
                            log('Error in second pass:', innerErr.message);
                        } finally {
                            // v1.18.15 EC-10: Always reset flags in finally block
                            isRefreshing = false;
                            window.spcbBranchRefreshInProgress = false;

                            // v1.18.17 EC-13: Final fallback - ensure DOM and Store are in sync
                            // This catches edge cases where forceSelectFirstVisibleRate() couldn't find a rate
                            setTimeout(() => {
                                // v1.18.27: Guard para prevenir race condition si checkout inició
                                if (window.spcbCheckoutLock?.isProcessing?.()) return;

                                try {
                                    const checkedInDom = document.querySelector(
                                        '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
                                    );
                                    if (checkedInDom && wpData) {
                                        const store = wpData.select('wc/store/cart');
                                        const rates = store?.getShippingRates?.();
                                        const selectedInStore = rates?.[0]?.shipping_rates?.find(r => r.selected);

                                        if (!selectedInStore || selectedInStore.rate_id !== checkedInDom.value) {
                                            log('EC-13 fallback: DOM has', checkedInDom.value, 'but Store has', selectedInStore?.rate_id || 'none');
                                            const cartDispatch = wpData.dispatch('wc/store/cart');
                                            if (cartDispatch?.selectShippingRate) {
                                                cartDispatch.selectShippingRate(checkedInDom.value, 0);
                                                log('EC-13 fallback: synced Store with DOM selection');
                                            }
                                        }
                                    }
                                } catch (ec13Err) {
                                    log('EC-13 fallback error:', ec13Err.message);
                                }
                            }, 100);
                        }
                    }, 200);
                } catch (outerErr) {
                    log('Error in first pass:', outerErr.message);
                    // v1.18.15 EC-10: Reset flags even on error
                    isRefreshing = false;
                    window.spcbBranchRefreshInProgress = false;
                }
            }, 300);

        } catch (e) {
            // v1.18.24 EC-5: AbortError es intencional (checkout inició), no es un error real
            if (e.name === 'AbortError') {
                log('Cart fetch aborted (checkout started)');
                isRefreshing = false;
                window.spcbBranchRefreshInProgress = false;
                return;  // No ejecutar fallback, el checkout manejará todo
            }

            log('Error fetching cart:', e.message);
            // v1.18.5: Ejecutar fallback ANTES de desbloquear
            forceSelectFirstVisibleRate();
            isRefreshing = false;
            window.spcbBranchRefreshInProgress = false;
        } finally {
            // v1.18.24 EC-5: Limpiar el controller después de que el fetch complete
            cartFetchController = null;
        }
    }

    /**
     * Force select the first visible shipping rate
     * v1.19.3: Simplified - just sync Store with DOM
     * v1.18.6: Added retry mechanism for when React hasn't rendered yet
     * v1.18.10: Respects current mode - in home mode, prefers non-SPCB rates
     */
    function forceSelectFirstVisibleRate(attempt = 1) {
        const MAX_ATTEMPTS = 5;
        const RETRY_DELAY = 150;

        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return;
        }

        const wpData = window.wp?.data;
        if (!wpData) return;

        // v1.18.10: Determine current mode from checkbox state
        const isBranchMode = checkboxElement?.checked ?? getStoredState();

        // Find first VISIBLE and checked radio, or first visible if none checked
        const allOptions = document.querySelectorAll(
            '.wc-block-components-shipping-rates-control .wc-block-components-radio-control__option'
        );

        let firstVisibleRateId = null;
        let firstVisibleSpcbRateId = null;
        let firstVisibleNonSpcbRateId = null;
        let checkedVisibleRateId = null;

        for (const option of allOptions) {
            const input = option.querySelector('input[type="radio"]');
            if (!input) continue;

            const isVisible = option.style.display !== 'none' &&
                              window.getComputedStyle(option).display !== 'none';

            if (!isVisible) continue;

            if (!firstVisibleRateId) {
                firstVisibleRateId = input.value;
            }

            if (!firstVisibleSpcbRateId && isSpcbRate(input.value)) {
                firstVisibleSpcbRateId = input.value;
            }

            // v1.18.10: Track first non-SPCB rate for home mode
            if (!firstVisibleNonSpcbRateId && !isSpcbRate(input.value)) {
                firstVisibleNonSpcbRateId = input.value;
            }

            // Track checked rate (any type, not just SPCB)
            if (input.checked) {
                checkedVisibleRateId = input.value;
            }
        }

        if (!firstVisibleRateId) {
            // v1.18.6: Retry if React hasn't rendered yet
            if (attempt < MAX_ATTEMPTS) {
                log(`No visible shipping rate found, retry ${attempt}/${MAX_ATTEMPTS}`);
                // v1.18.25: Usar trackedTimeout para poder cancelar en checkout
                trackedTimeout(() => forceSelectFirstVisibleRate(attempt + 1), RETRY_DELAY);
            } else {
                log('No visible shipping rate found after max attempts');
            }
            return;
        }

        // v1.18.10: If there's already a visible checked rate that matches the mode, keep it
        if (checkedVisibleRateId) {
            const checkedIsSpcb = isSpcbRate(checkedVisibleRateId);
            // In branch mode, keep SPCB rates; in home mode, keep non-SPCB rates
            if ((isBranchMode && checkedIsSpcb) || (!isBranchMode && !checkedIsSpcb)) {
                log('Keeping current checked rate:', checkedVisibleRateId, '(matches mode)');
                // Just ensure store is synced
                const dispatch = wpData.dispatch('wc/store/cart');
                if (dispatch?.selectShippingRate) {
                    dispatch.selectShippingRate(checkedVisibleRateId, 0);
                }
                return;
            }
        }

        // v1.18.10: Select rate based on mode
        let targetRateId;
        if (isBranchMode) {
            // Branch mode: prefer SPCB rates
            targetRateId = firstVisibleSpcbRateId || firstVisibleRateId;
        } else {
            // Home mode: prefer non-SPCB rates (Starken, SumeyExpress, etc.)
            targetRateId = firstVisibleNonSpcbRateId || firstVisibleRateId;
        }

        if (!targetRateId) {
            log('No suitable visible rate found, skipping auto-select');
            return;
        }

        log('Syncing Store to rate:', targetRateId, '(attempt', attempt, ', branchMode:', isBranchMode, ')');

        // Update Store
        const dispatch = wpData.dispatch('wc/store/cart');
        if (dispatch?.selectShippingRate) {
            // v1.18.10: Allow non-SPCB rates in home mode
            if (isSpcbRate(targetRateId)) {
                if (!shouldSkipProgrammaticSync(targetRateId, 'forceSelectFirstVisibleRate')) {
                    dispatch.selectShippingRate(targetRateId, 0);
                }
            } else {
                // Non-SPCB rate - sync directly (user-initiated mode change)
                dispatch.selectShippingRate(targetRateId, 0);
            }
        }

        // Ensure DOM is also updated
        const targetRadio = document.querySelector(
            `.wc-block-components-shipping-rates-control input[type="radio"][value="${targetRateId}"]`
        );
        if (targetRadio && !targetRadio.checked) {
            targetRadio.checked = true;
            targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // v1.18.6: Verify the store actually has the rate selected after a brief delay
        setTimeout(() => {
            // v1.18.27: Guard para prevenir race condition si checkout inició
            if (window.spcbCheckoutLock?.isProcessing?.()) return;

            const store = wpData.select('wc/store/cart');
            const rates = store?.getShippingRates?.();
            const selectedRate = rates?.[0]?.shipping_rates?.find(r => r.selected);

            if (!selectedRate || selectedRate.rate_id !== targetRateId) {
                log('Store verification failed, re-selecting:', targetRateId);
                dispatch?.selectShippingRate?.(targetRateId, 0);
            } else {
                log('Store verified, rate selected:', targetRateId);
            }
        }, 100);
    }

    /**
     * Select shipping method in WC store with retry mechanism
     * v1.18.0: Nueva función con guards y reintentos
     *
     * @param {string} rateId - The rate ID to select
     * @param {number} attempt - Current attempt number (default 1)
     */
    function syncShippingRateToStore(rateId, attempt = 1) {
        const MAX_ATTEMPTS = 3;
        const RETRY_DELAYS = [100, 200, 400];

        if (!isSpcbRate(rateId)) {
            log('Skipping sync for non-SPCB rate:', rateId);
            return;
        }

        // GUARD: No sincronizar durante el procesamiento del checkout
        // Esto evita race conditions con el submit
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('Checkout processing, skipping sync');
            return;
        }

        const wpData = window.wp?.data;
        if (!wpData) {
            if (attempt < MAX_ATTEMPTS) {
                // v1.18.25: Usar trackedTimeout para poder cancelar en checkout
                trackedTimeout(() => syncShippingRateToStore(rateId, attempt + 1), RETRY_DELAYS[attempt - 1]);
            }
            return;
        }

        try {
            const dispatch = wpData.dispatch('wc/store/cart');

            if (dispatch?.selectShippingRate) {
                if (attempt === 1 && shouldSkipProgrammaticSync(rateId, 'syncShippingRateToStore')) {
                    return;
                }
                dispatch.selectShippingRate(rateId, 0);
                log('Shipping rate synced to store:', rateId, 'attempt:', attempt);
            }

            // Verificación: confirmar que el store tiene el rate
            setTimeout(() => {
                // Re-check guard antes de verificar
                if (window.spcbCheckoutLock?.isProcessing?.()) return;

                const store = wpData.select('wc/store/cart');
                const shippingRates = store?.getShippingRates?.();
                if (shippingRates?.[0]?.shipping_rates) {
                    const selected = shippingRates[0].shipping_rates.find(r => r.selected);
                    if (!selected || selected.rate_id !== rateId) {
                        log('Store verification failed, retrying...');
                        if (attempt < MAX_ATTEMPTS) {
                            syncShippingRateToStore(rateId, attempt + 1);
                        }
                    } else {
                        log('Store verification passed:', rateId);
                    }
                }
            }, 100);

        } catch (e) {
            log('Error syncing shipping rate:', e.message);
            if (attempt < MAX_ATTEMPTS) {
                // v1.18.25: Usar trackedTimeout para poder cancelar en checkout
                trackedTimeout(() => syncShippingRateToStore(rateId, attempt + 1), RETRY_DELAYS[attempt - 1]);
            }
        }
    }

    /**
     * Select first visible shipping method with retry
     * v1.18.0: Reescrito con guards y reintentos
     * v1.18.9: Added allowAnyRate parameter for user-initiated actions
     * v1.18.10: Added preferSpcbRates parameter - false in home mode to avoid selecting "Otro"
     *
     * @param {number} attempt - Current attempt number (default 1)
     * @param {boolean} allowAnyRate - If true, allows selecting non-SPCB rates (for user actions)
     * @param {boolean} preferSpcbRates - If true, prefer SPCB rates; if false, prefer non-SPCB rates (default true)
     */
    function selectFirstVisibleShippingMethodWithRetry(attempt = 1, allowAnyRate = false, preferSpcbRates = true) {
        const MAX_ATTEMPTS = 3;
        const RETRY_DELAYS = [100, 200, 400];

        // GUARD: No ejecutar durante checkout
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('Checkout processing, skipping selectFirstVisible');
            return;
        }

        const allOptions = document.querySelectorAll(
            '.wc-block-components-shipping-rates-control .wc-block-components-radio-control__option'
        );

        let firstVisibleRateId = null;
        let firstVisibleSpcbRateId = null;
        let firstVisibleNonSpcbRateId = null;

        allOptions.forEach(option => {
            const input = option.querySelector('input[type="radio"]');
            if (!input) return;

            const isVisible = option.style.display !== 'none' &&
                              window.getComputedStyle(option).display !== 'none';
            if (!isVisible) return;

            if (!firstVisibleRateId) {
                firstVisibleRateId = input.value;
            }

            if (!firstVisibleSpcbRateId && isSpcbRate(input.value)) {
                firstVisibleSpcbRateId = input.value;
            }

            // v1.18.10: Track first non-SPCB rate for home mode
            if (!firstVisibleNonSpcbRateId && !isSpcbRate(input.value)) {
                firstVisibleNonSpcbRateId = input.value;
            }
        });

        if (!firstVisibleRateId) {
            if (attempt < MAX_ATTEMPTS) {
                // v1.18.25: Usar trackedTimeout para poder cancelar en checkout
                trackedTimeout(() => selectFirstVisibleShippingMethodWithRetry(attempt + 1, allowAnyRate, preferSpcbRates), RETRY_DELAYS[attempt - 1]);
            }
            return;
        }

        // v1.18.10: Rate selection logic
        // - If preferSpcbRates=true (branch mode): prefer SPCB rates
        // - If preferSpcbRates=false (home mode): prefer non-SPCB rates (regular shipping like Starken)
        let rateId;
        if (preferSpcbRates) {
            // Branch mode: prefer SPCB rates, fallback to any if allowAnyRate
            rateId = firstVisibleSpcbRateId || (allowAnyRate ? firstVisibleRateId : null);
        } else {
            // Home mode: prefer non-SPCB rates (Starken, SumeyExpress, etc.)
            // Only use SPCB rate as absolute last resort
            rateId = firstVisibleNonSpcbRateId || (allowAnyRate ? firstVisibleRateId : null);
        }

        if (!rateId) {
            log('No suitable visible rate found, skipping auto-select (allowAnyRate:', allowAnyRate, 'preferSpcbRates:', preferSpcbRates, ')');
            return;
        }

        log('Selecting first visible shipping method:', rateId, 'attempt:', attempt, 'allowAnyRate:', allowAnyRate, 'preferSpcbRates:', preferSpcbRates);

        // Asegurar que el DOM radio esté checked
        const targetRadio = document.querySelector(
            `.wc-block-components-shipping-rates-control input[type="radio"][value="${rateId}"]`
        );
        if (targetRadio && !targetRadio.checked) {
            targetRadio.checked = true;
            targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // v1.18.9: For user-initiated actions with non-SPCB rates, sync directly to store
        // without the SPCB-only guards (since this is a user action, not automatic sync)
        if (allowAnyRate && !isSpcbRate(rateId)) {
            const wpData = window.wp?.data;
            const dispatch = wpData?.dispatch('wc/store/cart');
            if (dispatch?.selectShippingRate) {
                dispatch.selectShippingRate(rateId, 0);
                log('User-initiated sync for non-SPCB rate:', rateId);
            }
        } else {
            // Sincronizar con el store (tiene su propio guard interno)
            syncShippingRateToStore(rateId, 1);
        }
    }

    /**
     * Select the first visible shipping method in WC Blocks store
     * v1.18.0: Ahora usa la versión con reintentos
     */
    function selectFirstVisibleShippingMethod() {
        selectFirstVisibleShippingMethodWithRetry(1);
    }

    // ============================================
    // DELIVERY MODE DETECTION
    // ============================================

    /**
     * Check if current delivery mode is "Shipping" (not "Local Pickup")
     * Returns true if user has selected "Enviar" option
     */
    function isShippingMode() {
        // Check for the shipping method option being selected
        // WooCommerce Blocks uses these selectors for Enviar/Retiro
        const shippingOption = document.querySelector('.wc-block-checkout__shipping-method-option--selected');
        if (!shippingOption) {
            // If no option is selected, check if shipping rates are visible
            // (rates only show when "Enviar" is selected)
            const shippingRates = document.querySelector('.wc-block-components-shipping-rates-control');
            return !!shippingRates;
        }

        // Check the input value - local_pickup methods contain "pickup" in their value
        const input = shippingOption.querySelector('input[type="radio"]');
        if (input) {
            const value = input.value || '';
            const isPickup = value.includes('pickup') || value.includes('local_pickup');
            return !isPickup;
        }

        // Fallback: check the label text
        const label = shippingOption.textContent || '';
        const isPickup = label.toLowerCase().includes('retiro') || label.toLowerCase().includes('pickup');
        return !isPickup;
    }

    /**
     * Toggle checkbox visibility based on delivery mode
     */
    function updateCheckboxVisibility() {
        const container = document.querySelector('.spcb-branch-checkbox-container');
        if (!container) return;

        const shouldShow = isShippingMode();
        container.style.display = shouldShow ? '' : 'none';

        log('Checkbox visibility updated:', shouldShow ? 'visible' : 'hidden', '(isShippingMode:', shouldShow, ')');

        // If hiding and checkbox was checked, reset state
        if (!shouldShow && checkboxElement && checkboxElement.checked) {
            checkboxElement.checked = false;
            handleCheckboxChange(false);
            saveState(false);
            log('Checkbox unchecked due to pickup mode');
        }
    }

    /**
     * Setup listeners for delivery mode changes
     */
    function setupDeliveryModeListeners() {
        // Listen for clicks on shipping method options (Enviar / Retiro en tienda)
        document.addEventListener('click', (e) => {
            const option = e.target.closest('.wc-block-checkout__shipping-method-option');
            if (option) {
                // Delay to let WC Blocks update the selected state
                setTimeout(updateCheckboxVisibility, 50);
            }
        });

        // Also listen for changes on the radio inputs
        document.addEventListener('change', (e) => {
            if (e.target.matches('.wc-block-checkout__shipping-method-option input[type="radio"]')) {
                setTimeout(updateCheckboxVisibility, 50);
            }
        });
    }

    // ============================================
    // CHECKBOX INJECTION
    // ============================================

    /**
     * Inject the branch checkbox after the Entrega section
     */
    function injectBranchCheckbox() {
        const config = window.spcbConfig || {};

        // Only if the option is enabled
        if (!config.branchCheckboxEnabled) {
            log('Branch checkbox is disabled in admin');
            return;
        }

        // Already injected?
        if (isCheckboxInjected || document.querySelector('.spcb-branch-checkbox-container')) {
            log('Branch checkbox already injected');
            // Still update visibility in case delivery mode changed
            updateCheckboxVisibility();
            return;
        }

        // Find the shipping method block (Entrega section)
        const shippingMethodBlock = document.querySelector('#shipping-method');
        if (!shippingMethodBlock) {
            log('Shipping method block not found yet');
            return;
        }

        // Get the closest fieldset (the Entrega container)
        const entregaSection = shippingMethodBlock.closest('fieldset.wc-block-checkout__shipping-method');
        if (!entregaSection) {
            log('Entrega section fieldset not found');
            return;
        }

        // Get checkbox text from config
        const checkboxText = config.branchCheckboxText ||
            'Quiero que mi envío llegue a la sucursal de la agencia (no a mi domicilio)';

        // Build checkbox HTML
        const checkboxHtml = `
            <div class="spcb-branch-checkbox-container" style="
                margin: 16px 0 8px;
                padding: 16px;
                background: #f8f9fa;
                border: 1px solid #e9ecef;
                border-radius: 8px;
            ">
                <label class="spcb-branch-checkbox-label" style="
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    cursor: pointer;
                    font-size: 14px;
                    line-height: 1.5;
                ">
                    <input
                        type="checkbox"
                        id="spcb-branch-checkbox"
                        class="spcb-branch-checkbox-input"
                        style="
                            width: 20px;
                            height: 20px;
                            margin-top: 2px;
                            flex-shrink: 0;
                            accent-color: var(--spcb-accent, #008060);
                        "
                    >
                    <span class="spcb-branch-checkbox-text">
                        <strong style="display: block; margin-bottom: 4px;">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px;">
                                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            Envío a Sucursal
                        </strong>
                        <span style="color: #666;">${checkboxText}</span>
                    </span>
                </label>
            </div>
        `;

        // Insert after the Entrega section content
        const contentDiv = entregaSection.querySelector('.wc-block-components-checkout-step__content');
        if (contentDiv) {
            contentDiv.insertAdjacentHTML('beforeend', checkboxHtml);
        } else {
            entregaSection.insertAdjacentHTML('beforeend', checkboxHtml);
        }

        // Get reference to checkbox
        checkboxElement = document.getElementById('spcb-branch-checkbox');
        if (!checkboxElement) {
            log('Failed to find injected checkbox');
            return;
        }

        // Set initial state from storage
        const storedState = getStoredState();
        checkboxElement.checked = storedState;

        // Apply initial state
        handleCheckboxChange(storedState);

        // Setup event listener
        checkboxElement.addEventListener('change', function() {
            const isChecked = this.checked;
            log('Checkbox changed:', isChecked);
            saveState(isChecked);
            handleCheckboxChange(isChecked);

            // v1.17.3: Refresh shipping rates from server (with guards)
            // This ensures PHP filterMethodsByBranchCheckbox() sees the new cookie state
            safeRefreshShippingRates();
        });

        isCheckboxInjected = true;
        log('Branch checkbox injected successfully');

        // Expose globally for fetch interceptor
        window.spcbBranchCheckbox = {
            isChecked: () => checkboxElement ? checkboxElement.checked : false,
            setChecked: (val) => {
                if (checkboxElement) {
                    checkboxElement.checked = val;
                    handleCheckboxChange(val);
                    saveState(val);
                    safeRefreshShippingRates();
                }
            }
        };

        // Update visibility based on current delivery mode
        updateCheckboxVisibility();
    }

    // ============================================
    // CHECKBOX STATE HANDLING
    // ============================================

    /**
     * Force expansion of the address form if it's collapsed (card mode)
     * WooCommerce Blocks shows a summary card with an "Edit" button when
     * the user has a saved address. We need to expand the form so the user
     * can see and interact with the visible fields (region, comuna, reference).
     *
     * @param {Function} callback - Optional callback to execute after form expands
     */
    function forceAddressFormExpansion(callback) {
        // Find the "Edit" button in collapsed state
        const editButton = document.querySelector(
            '#shipping-fields .wc-block-components-address-card__edit[aria-expanded="false"]'
        );

        if (editButton) {
            log('Forcing address form expansion for branch mode');
            // Use the same mechanism WC Blocks uses - click the edit button
            editButton.click();

            // Wait for React to render the form before executing callback
            if (callback) {
                setTimeout(callback, 300);
            }
        } else if (callback) {
            // No edit button - form might be expanded OR not rendered yet
            // Check if shipping-fields has content
            const shippingFields = document.getElementById('shipping-fields');
            const hasContent = shippingFields && (
                shippingFields.querySelector('.wc-block-components-address-form') ||
                shippingFields.querySelector('.wc-block-components-address-address-wrapper')
            );

            if (hasContent) {
                // Form exists, execute callback
                log('Address form already expanded, executing callback');
                callback();
            } else {
                // Form not rendered yet, wait for it using MutationObserver
                log('Address form not rendered yet, waiting...');
                waitForAddressForm(callback);
            }
        }
    }

    /**
     * Wait for the address form to be rendered using MutationObserver
     * @param {Function} callback - Function to call when form appears
     * @param {number} timeout - Maximum wait time in ms (default 6000)
     */
    function waitForAddressForm(callback, timeout = 6000) {
        const startTime = Date.now();

        const checkForm = () => {
            const shippingFields = document.getElementById('shipping-fields');
            const hasContent = shippingFields && (
                shippingFields.querySelector('.wc-block-components-address-form') ||
                shippingFields.querySelector('.wc-block-components-address-address-wrapper')
            );

            if (hasContent) {
                log('Address form appeared, executing callback');
                callback();
                return true;
            }
            return false;
        };

        // Check immediately
        if (checkForm()) return;

        // Setup observer
        const observer = new MutationObserver(() => {
            if (checkForm()) {
                observer.disconnect();
            } else if (Date.now() - startTime > timeout) {
                log('Timeout waiting for address form');
                observer.disconnect();
                // Execute callback anyway - maybe it can handle the missing form
                callback();
            }
        });

        // Observe shipping-fields or body
        const target = document.getElementById('shipping-fields') || document.body;
        observer.observe(target, {
            childList: true,
            subtree: true
        });

        // Also set a timeout as fallback
        setTimeout(() => {
            observer.disconnect();
            if (!checkForm()) {
                log('Fallback timeout reached, executing callback anyway');
                callback();
            }
        }, timeout);
    }

    /**
     * Apply branch mode state (called after form expansion)
     */
    function applyBranchModeState() {
        toggleAddressFields(true);

        // Reset flag to ensure we can inject the reference field
        isReferenceFieldInjected = false;
        toggleReferenceField(true);

        updateShippingTitle(true);
        filterShippingMethods(true);

        log('Branch mode state applied after form expansion');
    }

    /**
     * Handle checkbox state change
     */
    function handleCheckboxChange(isChecked) {
        log('Handling checkbox change:', isChecked);

        // Toggle body class
        if (isChecked) {
            document.body.classList.add('spcb-branch-mode');
            document.body.classList.remove('spcb-home-mode');

            // Force expansion first, then apply rest AFTER form renders
            forceAddressFormExpansion(applyBranchModeState);
        } else {
            document.body.classList.remove('spcb-branch-mode');
            document.body.classList.add('spcb-home-mode');

            toggleAddressFields(false);
            toggleReferenceField(false);
            updateShippingTitle(false);
            filterShippingMethods(false);
        }

        // Dispatch custom event for other modules
        document.dispatchEvent(new CustomEvent('spcb:branchModeChanged', {
            detail: { isBranchMode: isChecked }
        }));
    }

    /**
     * Update the shipping address section title based on branch mode
     */
    function updateShippingTitle(isBranchMode) {
        const titleElement = document.querySelector(
            '#shipping-fields .wc-block-components-checkout-step__title'
        );

        if (titleElement) {
            titleElement.textContent = isBranchMode
                ? 'Dirección de la sucursal'
                : 'Dirección de envío a domicilio';
            log('Updated shipping title:', titleElement.textContent);
        }
    }

    /**
     * Toggle visibility of address fields
     * Uses setProperty with 'important' to override CSS !important rules
     */
    /**
     * Placeholder for address_1 in branch mode
     * v1.17.8: Incluye número "1" para pasar validación backend en casos donde
     * se cambia de Factura a Boleta mientras branch mode está activo
     */
    const BRANCH_ADDRESS_PLACEHOLDER = 'Envío a Sucursal 1';

    function toggleAddressFields(hide) {
        // Fields to hide within #shipping-fields (branch mode = solo región, comuna, calle referencia)
        const fieldsToHide = [
            '.wc-block-components-address-form__country',
            '.wc-block-components-address-form__first_name',
            '.wc-block-components-address-form__last_name',
            '.wc-block-components-address-form__company',
            '.wc-block-components-address-form__address_1',
            '.wc-block-components-address-form__address_2',
            '.wc-block-components-address-form__phone',
            '.spcb-phone-shadow'  // Our custom phone input
        ];

        fieldsToHide.forEach(selector => {
            const fields = document.querySelectorAll(`#shipping-fields ${selector}`);
            fields.forEach(field => {
                if (hide) {
                    // Use setProperty with 'important' to override CSS !important
                    field.style.setProperty('display', 'none', 'important');
                    // Make field not required by clearing validation (for inputs inside divs)
                    const input = field.querySelector('input');
                    if (input) {
                        input.removeAttribute('required');
                        input.setAttribute('data-spcb-hidden', 'true');

                        // CRITICAL: For address_1, fill with placeholder to pass WC validation
                        // This solves both client-side and server-side validation
                        if (selector.includes('address_1')) {
                            // Store original value before replacing
                            if (!input.dataset.spcbOriginalValue && input.value) {
                                input.dataset.spcbOriginalValue = input.value;
                            }
                            // Fill with placeholder that includes a number (for backend validation)
                            input.value = BRANCH_ADDRESS_PLACEHOLDER;
                            // Trigger React state update
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                            input.dispatchEvent(new Event('change', { bubbles: true }));
                            log('Filled address_1 with placeholder:', BRANCH_ADDRESS_PLACEHOLDER);
                        }
                    }
                } else {
                    // Remove property to let CSS take control
                    field.style.removeProperty('display');
                    const input = field.querySelector('input');
                    if (input) {
                        input.removeAttribute('data-spcb-hidden');

                        // v1.17.8: Para address_1, siempre verificar y limpiar placeholder
                        // Usa includes() para mayor robustez (React puede modificar el valor)
                        if (selector.includes('address_1')) {
                            if (input.dataset.spcbOriginalValue) {
                                // Restaurar valor original guardado
                                input.value = input.dataset.spcbOriginalValue;
                                delete input.dataset.spcbOriginalValue;
                                log('Restored original address_1 value');
                            } else if (input.value.includes('Envío a Sucursal') || input.value === BRANCH_ADDRESS_PLACEHOLDER) {
                                // Limpiar placeholder (verificar con includes para mayor robustez)
                                input.value = '';
                                log('Cleared address_1 placeholder');
                            }
                            // Siempre disparar eventos para sincronizar React
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                            input.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                }
            });
        });

        // Handle address_2 toggle separately - search more broadly
        // The toggle "+ Agregar Depto/Piso/Oficina" may be outside #shipping-fields
        // CRITICAL: Use setProperty with 'important' because _base.css has display:flex!important
        const toggleSelectors = [
            '#shipping-fields .wc-block-components-address-form__address_2-toggle',
            '#shipping .wc-block-components-address-form__address_2-toggle',
            '.wc-block-components-address-form__address_2-toggle'  // Fallback: any toggle in the page
        ];

        toggleSelectors.forEach(selector => {
            const toggles = document.querySelectorAll(selector);
            toggles.forEach(toggle => {
                if (hide) {
                    // MUST use setProperty with 'important' to beat CSS !important
                    toggle.style.setProperty('display', 'none', 'important');
                    log('Hidden toggle with !important:', selector);
                } else {
                    toggle.style.removeProperty('display');
                }
            });
        });

        log('Address fields visibility toggled:', hide ? 'hidden' : 'visible');
    }

    /**
     * Toggle the reference field
     */
    function toggleReferenceField(show) {
        let referenceContainer = document.querySelector('.spcb-reference-field-container');

        if (show) {
            if (!referenceContainer) {
                injectReferenceField();
            } else {
                referenceContainer.style.display = '';
            }
        } else {
            if (referenceContainer) {
                referenceContainer.style.display = 'none';
            }
        }
    }

    /**
     * Inject the reference field into the shipping address section
     * NOTE: We inject OUTSIDE the form wrapper to avoid overflow:hidden issues
     * Uses MutationObserver to wait for the address wrapper to appear
     */
    function injectReferenceField(retryCount = 0) {
        const MAX_RETRIES = 30; // 30 * 200ms = 6 seconds max

        if (isReferenceFieldInjected) return;
        if (document.querySelector('.spcb-reference-field-container')) {
            isReferenceFieldInjected = true;
            log('Reference field already exists');
            return;
        }

        // Find the address wrapper (container of the form)
        // We inject AFTER the wrapper (not inside) to avoid affecting grid layout
        const addressWrapper = document.querySelector('#shipping-fields .wc-block-components-address-address-wrapper');
        if (!addressWrapper) {
            if (retryCount >= MAX_RETRIES) {
                log('Max retries reached for reference field injection');
                // Fallback: try to inject in shipping-fields directly
                const shippingFields = document.getElementById('shipping-fields');
                if (shippingFields) {
                    log('Falling back to shipping-fields container');
                    injectReferenceFieldIntoContainer(shippingFields);
                }
                return;
            }

            log('Address wrapper not found, retry', retryCount + 1, 'of', MAX_RETRIES);
            setTimeout(() => {
                injectReferenceField(retryCount + 1);
            }, 200);
            return;
        }

        log('Address wrapper found, injecting reference field after it');
        injectReferenceFieldIntoContainer(addressWrapper, 'afterend');
    }

    /**
     * Actually inject the reference field HTML into/after a container
     */
    function injectReferenceFieldIntoContainer(container, position = 'beforeend') {
        // v1.18.15 EC-11: Prevent race condition between injectReferenceField and guaranteedReferenceFieldInjection
        if (isReferenceFieldInjectionInProgress) {
            log('Reference field injection already in progress, skipping');
            return;
        }
        if (isReferenceFieldInjected || document.querySelector('.spcb-reference-field-container')) {
            isReferenceFieldInjected = true;
            return;
        }

        isReferenceFieldInjectionInProgress = true;

        // v1.18.16: Use try/finally per auditor feedback to guarantee flag reset even on error
        try {
            // Get stored reference value
            const storedReference = localStorage.getItem(REFERENCE_STORAGE_KEY) || '';

            // Build reference field HTML (styled like WC Blocks fields)
            // Simple HTML - styles in CSS file
            const referenceHtml = `
                <div class="spcb-reference-field-container">
                    <input
                        type="text"
                        id="${REFERENCE_INPUT_ID_INLINE}"
                        data-spcb-branch-reference="inline"
                        class="spcb-reference-input"
                        placeholder=" "
                        value="${storedReference}"
                        autocomplete="off"
                    >
                    <label for="${REFERENCE_INPUT_ID_INLINE}" class="spcb-reference-label">
                        Calle de referencia (Opcional)
                    </label>
                    <p class="spcb-field-description">
                        Indica una calle cercana a la sucursal donde retirarás tu pedido.
                    </p>
                </div>
            `;

            // Insert at specified position
            container.insertAdjacentHTML(position, referenceHtml);

            // Setup save on change - v1.17.13: More robust saving with multiple events
            const referenceInput = document.querySelector(REFERENCE_INPUT_SELECTOR_INLINE);
            if (referenceInput) {
                // Save to localStorage on multiple events for robustness
                const saveToStorage = function() {
                    const value = this.value || '';
                    localStorage.setItem(REFERENCE_STORAGE_KEY, value);
                    setCookie(REFERENCE_COOKIE_NAME, encodeURIComponent(value), 1);
                    log('Branch reference saved to localStorage:', value);
                };

                referenceInput.addEventListener('input', saveToStorage);
                referenceInput.addEventListener('change', saveToStorage);
                referenceInput.addEventListener('blur', function() {
                    saveToStorage.call(this);
                    if (!this.value) {
                        this.closest('.spcb-reference-field-container').classList.remove('is-active');
                    }
                });

                // Add is-active class when has value (for label positioning)
                if (storedReference) {
                    referenceInput.closest('.spcb-reference-field-container').classList.add('is-active');
                    setCookie(REFERENCE_COOKIE_NAME, encodeURIComponent(storedReference), 1);
                }

                referenceInput.addEventListener('focus', function() {
                    this.closest('.spcb-reference-field-container').classList.add('is-active');
                });

                // Note: Global submit sync is handled by setupBranchReferenceSubmitSync()
            }

            isReferenceFieldInjected = true;
            log('Reference field injected');
        } catch (err) {
            log('Error injecting reference field:', err.message);
        } finally {
            // v1.18.16: ALWAYS reset flag, even if error occurs
            isReferenceFieldInjectionInProgress = false;
        }
    }

    // ============================================
    // MANUAL SHIPPING CHANGE LISTENER
    // ============================================

    /**
     * Setup listener for manual shipping method changes
     * v1.18.0: Ensures store stays in sync when user clicks a different rate
     * v1.18.7: Restored e.isTrusted guard (was removed by mistake in v1.18.5)
     */
    function setupManualShippingChangeListener() {
        document.addEventListener('change', function(e) {
            // v1.18.7: Ignorar eventos generados por script (dispatchEvent)
            // e.isTrusted = true solo para acciones reales del usuario (click, tecla)
            // e.isTrusted = false para eventos programáticos (dispatchEvent)
            // Esto rompe el bucle de retroalimentación creado en v1.17.11:
            // - selectFirstVisibleShippingMethodWithRetry() dispara change event
            // - syncDomWithStore() dispara change event
            // Sin este guard, ambos eventos causan llamadas redundantes a selectShippingRate()
            // NOTA: Este guard fue agregado en v1.18.2 pero eliminado por error en v1.18.5
            //       al revertir a v1.18.1 para solucionar otro bug
            if (!e.isTrusted) {
                return;
            }

            // GUARD: No sincronizar durante checkout
            if (window.spcbCheckoutLock?.isProcessing?.()) {
                log('Checkout processing, skipping manual sync');
                return;
            }

            // Verificar si es un radio de shipping rates
            const radio = e.target.closest(
                '.wc-block-components-shipping-rates-control input[type="radio"]'
            );

            if (!radio) return;

            const rateId = radio.value;
            log('Manual shipping method change detected:', rateId);

            if (!isSpcbRate(rateId)) {
                log('Manual change for non-SPCB rate, skipping sync:', rateId);
                return;
            }

            // Sincronizar con el store (con reintentos)
            syncShippingRateToStore(rateId, 1);
        });

        log('Manual shipping change listener setup');
    }

    // ============================================
    // SHIPPING METHOD FILTERING
    // ============================================

    /**
     * Filter shipping methods based on checkbox state
     * v1.18.0: Usa selectFirstVisibleShippingMethodWithRetry en vez de .click()
     */
    function filterShippingMethods(isBranchMode) {
        const config = window.spcbConfig || {};
        const methodsToHide = isBranchMode
            ? (config.methodsHideWhenBranch || [])
            : (config.methodsHideWhenHome || []);

        log('Filtering shipping methods. Branch mode:', isBranchMode, 'Methods to hide:', methodsToHide);

        if (methodsToHide.length === 0) {
            // Show all methods
            const allOptions = document.querySelectorAll('.wc-block-components-radio-control__option');
            allOptions.forEach(option => {
                option.style.display = '';
            });
            return;
        }

        // Get all shipping rate options
        const allOptions = document.querySelectorAll('.wc-block-components-radio-control__option');
        let needsNewSelection = false;

        allOptions.forEach(option => {
            const input = option.querySelector('input[type="radio"]');
            if (!input) return;

            const rateId = input.value;

            // Check if this method should be hidden
            // v1.17.3: Match by full rate ID only (consistent with PHP filterMethodsByBranchCheckbox)
            const shouldHide = methodsToHide.some(methodId => rateId === methodId);

            if (shouldHide) {
                option.style.display = 'none';

                // v1.18.0: Mark that we need to select a new method
                if (input.checked) {
                    needsNewSelection = true;
                }
            } else {
                option.style.display = '';
            }
        });

        // v1.18.0: Si el método seleccionado fue ocultado, seleccionar el primero visible
        // Usa la función con reintentos en vez de .click()
        // v1.18.9: allowAnyRate=true because this is a user-initiated action (checkbox change)
        // v1.18.10: preferSpcbRates=isBranchMode
        //   - In branch mode (checkbox checked): prefer SPCB rates (spcb_branch_shipping, etc.)
        //   - In home mode (checkbox unchecked): prefer non-SPCB rates (Starken, SumeyExpress, etc.)
        //   This prevents "Otro" (spcb_other_shipping) from being selected in home mode
        if (needsNewSelection) {
            selectFirstVisibleShippingMethodWithRetry(1, true, isBranchMode);
        }
    }

    // ============================================
    // TOGGLE HIDER - Aggressive observer for address_2 toggle
    // ============================================

    /**
     * Hide the address_2 toggle immediately (no debounce)
     * This runs separately from the main observer to prevent flash
     * Uses setProperty with 'important' to override CSS !important
     */
    function hideAddress2Toggle() {
        if (!checkboxElement || !checkboxElement.checked) return;

        // Search with multiple selectors
        const selectors = [
            '#shipping-fields .wc-block-components-address-form__address_2-toggle',
            '#shipping .wc-block-components-address-form__address_2-toggle',
            '.wc-block-components-address-form__address_2-toggle'
        ];

        selectors.forEach(selector => {
            const toggles = document.querySelectorAll(selector);
            toggles.forEach(toggle => {
                // Use setProperty with 'important' to beat CSS !important
                toggle.style.setProperty('display', 'none', 'important');
            });
        });

        log('Immediately hid address_2 toggle with !important');
    }

    /**
     * Setup a dedicated observer for the address_2 toggle
     * This observer runs WITHOUT debounce to prevent any flash
     */
    function setupToggleHider() {
        const shippingFields = document.getElementById('shipping-fields');
        if (!shippingFields) return;

        const toggleObserver = new MutationObserver(() => {
            // Immediately hide toggle if in branch mode - no debounce
            hideAddress2Toggle();
        });

        toggleObserver.observe(shippingFields, {
            childList: true,
            subtree: true
        });

        log('Toggle hider observer setup');
    }

    // ============================================
    // BRANCH REFERENCE SUBMIT SYNC
    // ============================================

    let submitSyncSetup = false;

    /**
     * Setup global listener to sync branch reference value before checkout submit
     * v1.17.13: This ensures the value is saved to localStorage even if input events didn't fire
     * v1.19.2: Also syncs shipping rate selection as last-resort guard
     */
    function setupBranchReferenceSubmitSync() {
        if (submitSyncSetup) return;
        submitSyncSetup = true;

        // Listen for checkout submit button clicks (capture phase to run BEFORE submit)
        document.addEventListener('click', function(e) {
            const submitBtn = e.target.closest('.wc-block-components-checkout-place-order-button');
            if (!submitBtn) return;

            // 1. Sync branch reference
            const referenceInput = document.querySelector(REFERENCE_INPUT_SELECTOR_INLINE) ||
                                   document.querySelector('#' + REFERENCE_INPUT_ID_INLINE) ||
                                   document.querySelector('.spcb-reference-field-container input.spcb-reference-input');

            if (referenceInput && referenceInput.value) {
                localStorage.setItem(REFERENCE_STORAGE_KEY, referenceInput.value);
                setCookie(REFERENCE_COOKIE_NAME, encodeURIComponent(referenceInput.value), 1);
                log('Branch reference synced before submit:', referenceInput.value);
            }

            // 2. v1.18.5: COMENTADO - Esta llamada causaba duplicados durante el submit
            // El Store ya está sincronizado por receiveCart() + forceSelectFirstVisibleRate()
            // syncShippingRateBeforeSubmit();

        }, true); // Capture phase

        log('Branch reference submit sync setup');
    }

    /**
     * Last-resort sync of shipping rate before checkout submit
     * v1.19.2: Ensures Store has the same rate that user sees selected in DOM
     *
     * This fixes the "necesita una opción de envío" error that happens when:
     * 1. DOM shows Starken selected (checked radio)
     * 2. But Store has different/no rate selected
     * 3. On submit, WC validates Store's selection → ERROR
     */
    function syncShippingRateBeforeSubmit() {
        const wpData = window.wp?.data;
        if (!wpData) {
            log('syncShippingRateBeforeSubmit: wp.data not available');
            return;
        }

        // Find the CHECKED radio in DOM (what user sees)
        const checkedRadio = document.querySelector(
            '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
        );

        if (!checkedRadio) {
            log('syncShippingRateBeforeSubmit: No radio checked in DOM');
            // Try to select first visible
            forceSelectFirstVisibleRate();
            return;
        }

        const domSelectedRateId = checkedRadio.value;
        log('syncShippingRateBeforeSubmit: DOM has selected:', domSelectedRateId);

        // Check what Store has
        const store = wpData.select('wc/store/cart');
        const rates = store?.getShippingRates?.();
        const storeSelectedRate = rates?.[0]?.shipping_rates?.find(r => r.selected);

        log('syncShippingRateBeforeSubmit: Store has selected:', storeSelectedRate?.rate_id);

        // If Store doesn't match DOM, force sync
        if (storeSelectedRate?.rate_id !== domSelectedRateId) {
            log('syncShippingRateBeforeSubmit: MISMATCH! Forcing sync to:', domSelectedRateId);

            const dispatch = wpData.dispatch('wc/store/cart');
            if (dispatch?.selectShippingRate) {
                dispatch.selectShippingRate(domSelectedRateId, 0);
                log('syncShippingRateBeforeSubmit: Store updated');
            }
        } else {
            log('syncShippingRateBeforeSubmit: Store matches DOM, all good');
        }
    }

    // ============================================
    // INITIALIZATION
    // ============================================

    /**
     * Initialize the module
     */
    function init() {
        log('Initializing branch checkbox module');

        const config = window.spcbConfig || {};
        if (!config.branchCheckboxEnabled) {
            log('Branch checkbox is disabled');
            return;
        }

        // v1.18.0: Setup listener for manual shipping method changes
        setupManualShippingChangeListener();

        // v1.18.24 EC-5: Subscribe al checkout lock para cancelar fetches en progreso
        // Esto previene race conditions donde GET /cart y POST /checkout se ejecutan simultáneamente
        if (typeof window.spcbCheckoutLock?.subscribe === 'function') {
            window.spcbCheckoutLock.subscribe((isProcessing) => {
                if (isProcessing) {
                    // v1.18.24: Cancelar fetch GET /cart en progreso
                    if (cartFetchController) {
                        log('Checkout started, aborting cart fetch');
                        cartFetchController.abort();
                        cartFetchController = null;
                        isRefreshing = false;
                        window.spcbBranchRefreshInProgress = false;
                    }

                    // v1.18.25: Cancelar todos los timeouts pendientes
                    // Esto previene que selectShippingRate() (POST al servidor)
                    // se ejecute mientras el checkout está procesando
                    cancelAllPendingTimeouts();
                }
            });
            log('Subscribed to checkout lock for fetch/timeout cancellation');
        }

        // Setup delivery mode listeners (Enviar vs Retiro en tienda)
        setupDeliveryModeListeners();

        // v1.17.13: Setup global listener to sync branch reference before checkout submit
        // This ensures the value is in localStorage even if input events didn't fire properly
        setupBranchReferenceSubmitSync();

        // Try to inject immediately
        injectBranchCheckbox();

        // Also try after delays (for React hydration)
        [100, 300, 500, 1000, 2000, 3000].forEach(delay => {
            setTimeout(() => {
                if (!isCheckboxInjected) {
                    injectBranchCheckbox();
                }
                // Also update visibility on each retry
                updateCheckboxVisibility();
                // Setup toggle hider once shipping-fields exists
                setupToggleHider();

                // CRITICAL: Re-apply field visibility if checkbox is already checked
                // This handles the case where WC Blocks renders fields AFTER our init
                if (checkboxElement && checkboxElement.checked) {
                    // Force expansion first, then apply state after React renders
                    forceAddressFormExpansion(() => {
                        toggleAddressFields(true);

                        // Reset flag if reference field doesn't exist, so we can retry injection
                        if (!document.querySelector('.spcb-reference-field-container')) {
                            isReferenceFieldInjected = false;
                        }
                        toggleReferenceField(true);

                        updateShippingTitle(true);
                        log('Re-applied branch mode state after delay:', delay);
                    });
                }
            }, delay);
        });

        // v1.18.12: Sync shipping rate Store with DOM on initial page load
        // This fixes "Lo siento, este pedido necesita una opción de envío" error
        // when user has saved data and reloads the page - DOM shows a rate checked
        // but WC Blocks Store doesn't have selectedRate set
        [500, 1500, 3000].forEach(delay => {
            setTimeout(() => {
                // v1.18.28: Guard para prevenir race condition si checkout inició
                if (window.spcbCheckoutLock?.isProcessing?.()) return;

                // Only sync if there's a visible checked radio but Store might be out of sync
                const checkedRadio = document.querySelector(
                    '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
                );
                if (checkedRadio) {
                    log('Initial page load: syncing Store with checked radio:', checkedRadio.value);
                    forceSelectFirstVisibleRate();
                }
            }, delay);
        });

        // Watch for DOM changes (WC Blocks might re-render)
        const observer = new MutationObserver((mutations) => {
            // IMMEDIATE: Hide toggle without debounce
            hideAddress2Toggle();

            // Debounce the rest
            clearTimeout(observer._debounce);
            observer._debounce = setTimeout(() => {
                if (!isCheckboxInjected || !document.querySelector('.spcb-branch-checkbox-container')) {
                    isCheckboxInjected = false;
                    injectBranchCheckbox();
                }

                // Update checkbox visibility based on delivery mode
                updateCheckboxVisibility();

                // Re-apply field visibility if checkbox exists
                if (checkboxElement) {
                    const isChecked = checkboxElement.checked;
                    toggleAddressFields(isChecked);
                    if (isChecked && !document.querySelector('.spcb-reference-field-container')) {
                        isReferenceFieldInjected = false;
                        toggleReferenceField(true);
                    }
                }
            }, 100);
        });

        // Start observing
        const checkoutForm = document.querySelector('.wc-block-checkout');
        if (checkoutForm) {
            observer.observe(checkoutForm, {
                childList: true,
                subtree: true
            });
        }

        // Also observe body as fallback
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false
        });

        // Initial toggle hider setup
        setupToggleHider();
    }

    // Start when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Also try on window load
    window.addEventListener('load', () => {
        if (!isCheckboxInjected) {
            injectBranchCheckbox();
        }
    });

    // ============================================
    // GUARANTEED REFERENCE FIELD INJECTION
    // This is a failsafe that keeps trying until success
    // ============================================
    function guaranteedReferenceFieldInjection() {
        let attempts = 0;
        const maxAttempts = 50; // 50 * 200ms = 10 seconds max

        const tryInject = () => {
            attempts++;

            // Check if checkbox exists and is checked
            const checkbox = document.getElementById('spcb-branch-checkbox');
            const isChecked = checkbox ? checkbox.checked : getStoredState();

            if (!isChecked) {
                log('Guaranteed injection: checkbox not checked, stopping');
                return; // Stop if checkbox is not checked
            }

            // v1.18.15 EC-11: Check if another injection is in progress
            if (isReferenceFieldInjectionInProgress) {
                log('Guaranteed injection: another injection in progress, retrying...');
                if (attempts < maxAttempts) {
                    setTimeout(tryInject, 200);
                }
                return;
            }

            // Check if reference field already exists
            if (document.querySelector('.spcb-reference-field-container')) {
                log('Guaranteed injection: field already exists');
                isReferenceFieldInjected = true;
                return; // Already injected
            }

            // Find injection point - AFTER the wrapper (not inside) to avoid affecting grid layout
            const addressWrapper = document.querySelector('#shipping-fields .wc-block-components-address-address-wrapper');
            if (!addressWrapper) {
                if (attempts < maxAttempts) {
                    log('Guaranteed injection: waiting for wrapper, attempt', attempts);
                    setTimeout(tryInject, 200);
                } else {
                    log('Guaranteed injection: max attempts reached');
                }
                return;
            }

            // v1.18.15 EC-11: Set flag before injection
            // v1.18.16: Use try/finally per auditor feedback to guarantee flag reset
            isReferenceFieldInjectionInProgress = true;

            try {
                // Inject the field AFTER the wrapper
                log('Guaranteed injection: injecting reference field after wrapper');
                const storedReference = localStorage.getItem(REFERENCE_STORAGE_KEY) || '';
                // Simple HTML - styles in CSS file
                const referenceHtml = `
                    <div class="spcb-reference-field-container">
                        <input
                            type="text"
                            id="${REFERENCE_INPUT_ID_INLINE}"
                            data-spcb-branch-reference="inline"
                            class="spcb-reference-input"
                            placeholder=" "
                            value="${storedReference}"
                            autocomplete="off"
                        >
                        <label for="${REFERENCE_INPUT_ID_INLINE}" class="spcb-reference-label">
                            Calle de referencia (Opcional)
                        </label>
                        <p class="spcb-field-description">
                            Indica una calle cercana a la sucursal donde retirarás tu pedido.
                        </p>
                    </div>
                `;

                addressWrapper.insertAdjacentHTML('afterend', referenceHtml);
                isReferenceFieldInjected = true;

                // Setup input listener - v1.17.13: More robust saving
                const referenceInput = document.querySelector(REFERENCE_INPUT_SELECTOR_INLINE);
                if (referenceInput) {
                    const saveToStorage = function() {
                        const value = this.value || '';
                        localStorage.setItem(REFERENCE_STORAGE_KEY, value);
                        setCookie(REFERENCE_COOKIE_NAME, encodeURIComponent(value), 1);
                        log('Guaranteed injection: saved to localStorage:', value);
                    };

                    referenceInput.addEventListener('input', saveToStorage);
                    referenceInput.addEventListener('change', saveToStorage);
                    referenceInput.addEventListener('blur', function() {
                        saveToStorage.call(this);
                        if (!this.value) {
                            this.closest('.spcb-reference-field-container').classList.remove('is-active');
                        }
                    });

                    if (storedReference) {
                        referenceInput.closest('.spcb-reference-field-container').classList.add('is-active');
                        setCookie(REFERENCE_COOKIE_NAME, encodeURIComponent(storedReference), 1);
                    }
                    referenceInput.addEventListener('focus', function() {
                        this.closest('.spcb-reference-field-container').classList.add('is-active');
                    });
                }

                log('Guaranteed injection: SUCCESS');
            } catch (err) {
                log('Guaranteed injection: error during injection:', err.message);
            } finally {
                // v1.18.16: ALWAYS reset flag, even if error occurs
                isReferenceFieldInjectionInProgress = false;
            }
        };

        // Start trying after a short delay
        setTimeout(tryInject, 500);
    }

    // Run guaranteed injection on page load
    if (document.readyState === 'complete') {
        guaranteedReferenceFieldInjection();
    } else {
        window.addEventListener('load', guaranteedReferenceFieldInjection);
    }

    // ============================================
    // REFERENCE FIELD VISIBILITY ENFORCER
    // Ensures reference field exists when branch mode is active
    // ============================================
    function enforceReferenceFieldVisibility() {
        const checkbox = document.getElementById('spcb-branch-checkbox');
        const isChecked = checkbox ? checkbox.checked : getStoredState();

        if (!isChecked) return; // Only enforce when branch mode is active

        // Check if field already exists
        const referenceContainer = document.querySelector('.spcb-reference-field-container');
        if (!referenceContainer) {
            // CRITICAL: Reset flag to allow retry
            // The flag may have been set prematurely by a failed injection attempt
            isReferenceFieldInjected = false;

            // Field doesn't exist - force form expansion first, then inject
            // The form may be in collapsed "card mode" where addressWrapper doesn't exist
            forceAddressFormExpansion(() => {
                injectReferenceField();
            });
        }
    }

    // Run enforcer periodically for the first 5 seconds
    let enforceCount = 0;
    const enforceInterval = setInterval(() => {
        enforceReferenceFieldVisibility();
        enforceCount++;
        if (enforceCount >= 10) { // 10 * 500ms = 5 seconds
            clearInterval(enforceInterval);
        }
    }, 500);

    // Expose for external use
    window.spcbBranchCheckboxModule = {
        init,
        isChecked: () => checkboxElement ? checkboxElement.checked : getStoredState(),
        getReference: () => {
            const referenceInput = document.querySelector(REFERENCE_INPUT_SELECTOR_INLINE);
            if (referenceInput?.value) {
                return referenceInput.value;
            }
            try {
                return localStorage.getItem(REFERENCE_STORAGE_KEY) || '';
            } catch (e) {
                return '';
            }
        }
    };

})();
